import { AlertTriangle, Loader2 } from 'lucide-react';
import type { PickupOpportunity } from '@/types/pickupOpportunity';
import type { PickupOpportunityPricingEvidence } from '@/services/pickupOpportunityService';
import { cn } from '@/lib/utils';
import { formatPickupInvestorDisplayName } from '@/components/reports/pickupInvestorDisplay';
import { pickupDeltaPoints, pickupPointsToDollars } from '@/components/reports/pickupPricingMath';
import { buildPickupWaterfallReconciliation } from '@/components/reports/pickupWaterfallModel';
import {
  formatPickupCurrency,
  formatPickupPoints,
  formatPickupPrice
} from './pickupPipelineFormatters';

interface PickupPriceProvenanceProps {
  opportunity: PickupOpportunity;
  evidence: PickupOpportunityPricingEvidence | null;
  loading: boolean;
  error: string | null;
}

interface PricePathRowProps {
  label: string;
  price: number | null;
  delta: number | null;
  investor: string;
  winner?: boolean;
}

function PricePathRow({ label, price, delta, investor, winner = false }: PricePathRowProps) {
  const displayInvestor = formatPickupInvestorDisplayName(investor) || investor;
  return (
    <tr className={cn('border-t first:border-t-0', winner && 'bg-success/5')}>
      <th scope="row" className="py-1.5 pr-2 text-left font-normal">
        <span className={cn('block', winner && 'font-semibold text-foreground')}>{label}</span>
        <span className="mt-0.5 block truncate text-[10px] text-muted-foreground" title={displayInvestor}>
          {displayInvestor}
        </span>
      </th>
      <td className={cn(
        'py-1.5 text-right font-mono tabular-nums',
        winner && 'font-bold text-primary'
      )}>
        {formatPickupPrice(price)}
      </td>
      <td className={cn(
        'py-1.5 pl-2 text-right font-medium tabular-nums',
        delta !== null && delta > 0 ? 'text-success' : 'text-muted-foreground'
      )}>
        {delta === null ? 'Baseline' : formatPickupPoints(delta)}
      </td>
    </tr>
  );
}

function adjustmentTotal(values: Array<{ adjustor: number }>): number | null {
  return values.length > 0
    ? values.reduce((sum, item) => sum + Number(item.adjustor || 0), 0)
    : null;
}

function adjustmentValue(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return `${value > 0 ? '+' : ''}${value.toFixed(3)}`;
}

export function PickupPriceProvenance({
  opportunity,
  evidence,
  loading,
  error
}: PickupPriceProvenanceProps) {
  const currentDelta = pickupDeltaPoints(
    opportunity.latest_current_investor_price,
    opportunity.original_lock_price
  );
  const altDelta = pickupDeltaPoints(
    opportunity.latest_best_alt_price,
    opportunity.original_lock_price
  );
  const currentWins = currentDelta !== null && (altDelta === null || currentDelta >= altDelta);
  const winningDelta = currentWins ? currentDelta : altDelta;
  const winningInvestor = currentWins
    ? opportunity.original_investor
    : opportunity.latest_best_alt_investor || 'No qualifying alternative';
  const potentialDollars = pickupPointsToDollars(winningDelta, opportunity.loan_amount);
  const waterfall = evidence?.pricing_waterfall || null;
  const priceAdjustments = (waterfall?.adjustments || [])
    .filter((adjustment) => adjustment.adjustmentType === 'Price');
  const investorAdjustments = (waterfall?.investorAdjustments || [])
    .filter((adjustment) => adjustment.adjustmentType === 'Price');
  const llpaTotal = adjustmentTotal(priceAdjustments);
  const srpTotal = adjustmentTotal(investorAdjustments);
  const markupTotal = waterfall?.markups.length
    ? waterfall.markups.reduce((sum, markup) => sum + Number(markup.netMarkupPercent || 0), 0)
    : null;
  const llpaLabel = priceAdjustments.length > 0
    ? `LLPA — ${priceAdjustments.map(({ reason }) => reason).join(' · ')}`
    : 'LLPA';
  const waterfallReconciliation = waterfall
    ? buildPickupWaterfallReconciliation(waterfall, opportunity.original_lock_price)
    : null;

  return (
    <section aria-labelledby="pickup-price-provenance" className="min-w-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 id="pickup-price-provenance" className="text-[9.5px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
          Why the pickup is {formatPickupCurrency(potentialDollars)}
        </h3>
        <span className="text-[10px] text-muted-foreground">
          {opportunity.basis_lock_period}d basis · {Number(opportunity.original_lock_rate).toFixed(3)}%
        </span>
      </div>

      <table className="mt-2.5 w-full border-collapse text-[11.5px] leading-none">
        <tbody>
          <PricePathRow
            label="Locked buy-side price"
            price={opportunity.original_lock_price}
            delta={null}
            investor={opportunity.original_investor}
          />
          <PricePathRow
            label="Same investor today"
            price={opportunity.latest_current_investor_price}
            delta={currentDelta}
            investor={opportunity.original_investor}
            winner={currentWins}
          />
          <PricePathRow
            label="Best alternative"
            price={opportunity.latest_best_alt_price}
            delta={altDelta}
            investor={opportunity.latest_best_alt_investor || 'No qualifying alternative'}
            winner={!currentWins && altDelta !== null}
          />
        </tbody>
      </table>

      {currentDelta !== null && (
        <div className="mt-2 flex gap-1.5 text-[10.5px] leading-relaxed text-foreground/75">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
          <span>Same-investor pricing may be reduced by relock or re-submission costs. Confirm the executable price before negotiating.</span>
        </div>
      )}

      <div className="mt-3 rounded-lg border bg-card px-3 py-2.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-[9.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
            Stored locked buy-side waterfall · not the winning quote
          </span>
          <span className="text-[10px] text-muted-foreground">Winning route: {formatPickupInvestorDisplayName(winningInvestor) || winningInvestor}</span>
        </div>

        {loading && (
          <div className="flex min-h-24 items-center justify-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Loading stored price evidence…
          </div>
        )}
        {!loading && error && (
          <div role="alert" className="mt-2 border border-destructive/30 bg-destructive/5 p-2.5 text-xs text-destructive">
            {error}
          </div>
        )}
        {!loading && !error && waterfall && (
          <table
            className="mt-2 w-full border-collapse font-mono text-[11px] leading-none tabular-nums"
            data-pickup-waterfall-summary
            data-pickup-waterfall-base={waterfallReconciliation?.basePrice?.toFixed(3)}
            data-pickup-waterfall-net={waterfallReconciliation?.netPrice?.toFixed(3)}
            data-pickup-waterfall-reconciles={String(waterfallReconciliation?.reconciles)}
          >
            <tbody>
              <tr>
                <td className="py-1">Base price · {waterfall.pricingDetail?.lockPeriod || opportunity.basis_lock_period}d bucket</td>
                <td className="py-1 text-right">{formatPickupPrice(waterfall.pricingDetail?.parPrice)}</td>
              </tr>
              <tr>
                <td className="max-w-64 truncate py-1" title={llpaLabel}>{llpaLabel}</td>
                <td className="py-1 text-right text-destructive">{adjustmentValue(llpaTotal)}</td>
              </tr>
              <tr>
                <td className="py-1">SRP adjustment</td>
                <td className="py-1 text-right text-destructive">{adjustmentValue(srpTotal)}</td>
              </tr>
              {markupTotal !== null && (
                <tr>
                  <td className="py-1">Markup</td>
                  <td className="py-1 text-right text-destructive">{adjustmentValue(markupTotal)}</td>
                </tr>
              )}
              {waterfallReconciliation?.reconciliationPoints !== null
                && waterfallReconciliation?.reconciliationPoints !== undefined
                && Math.abs(waterfallReconciliation.reconciliationPoints) >= 0.0005 && (
                <tr>
                  <td className="py-1">Other / reconciliation</td>
                  <td className="py-1 text-right">
                    {adjustmentValue(waterfallReconciliation.reconciliationPoints)}
                  </td>
                </tr>
              )}
              <tr className="border-t font-bold text-foreground">
                <td className="pt-1.5">Locked net price</td>
                <td className="pt-1.5 text-right">{formatPickupPrice(opportunity.original_lock_price)}</td>
              </tr>
            </tbody>
          </table>
        )}
        {!loading && !error && !waterfall && (
          <p className="mt-2 text-[10.5px] leading-relaxed text-muted-foreground">
            Stored waterfall unavailable for this lock.
          </p>
        )}
        <p className="mt-2 text-[10px] leading-relaxed text-muted-foreground">
          This is the locked buy-side waterfall, not the winning quote. OB does not return the winning quote's adjustment waterfall, and this view makes no additional OB call.
        </p>
      </div>
    </section>
  );
}
